import React, { useCallback, useReducer, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'

import {
  makeStyles,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  IconButton
} from '@material-ui/core'
import ListIcon from '@material-ui/icons/List'

import { useLiterals } from '~/components/Literals'
import GridViewIcon from '~/components/GridViewIcon'

import ProductItem from './ProductItem'
import Placeholder from './Placeholder'

import actions from '~/actions'
import track from '~/track'
import ls from '~/localStorage'
import { selectProducts } from '~/selectors'

function getSlug(value) {
  return value
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
}

const useStyles = makeStyles((theme) => ({
  text: {
    marginRight: 15,
    '& span': {
      ...theme.gofind.lineLimit(2),
      maxHeight: 52,
      overflow: 'hidden'
    },
    '& p': {
      margin: 0,
      fontSize: 16,
      ...theme.gofind.lineLimit(2),
      maxHeight: 42
    }
  },
  root: {
    paddingTop: 0,
    paddingBottom: 0
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)'
  },
  buttonAnyProduct: {
    textTransform: 'none',
    backgroundColor: 'rgba(238, 235, 235, .9)'
  },
  buttonModeList: {
    borderRadius: 0,
    backgroundColor: 'rgba(238, 235, 235, .9)',
    padding: '6px 8px',
    transition: 'background-color 0.2s ease-in-out',

    '&:hover': { backgroundColor: '#D5D5D5' }
  }
}))

function reducer(state, { type, ...payload }) {
  return { ...state, ...payload }
}

function AnyProduct({ state, classes, onClick, onChange, getLiteral }) {
  const config = useSelector((state) => state.config)

  return (
    <>
      <ListItem style={{ background: '#F5F5F5', padding: 8 }}>
        <ListItemText className={classes.text}>
          <Button
            variant="contained"
            className={classes.buttonAnyProduct}
            disableElevation
            onClick={onClick}
          >
            {config.listingPlaceholderStore || `${getLiteral('1054')} ${getLiteral('1996')}`}
          </Button>
        </ListItemText>
        <ListItemIcon style={{ minWidth: 'unset' }}>
          {state.mode === 'LIST_VIEW' ? (
            <IconButton
              color="default"
              className={classes.buttonModeList}
              onClick={() => onChange('GRID_VIEW')}
            >
              <GridViewIcon style={{ fontSize: 24, color: '#000' }} />
            </IconButton>
          ) : (
            <IconButton
              color="default"
              className={classes.buttonModeList}
              onClick={() => onChange('LIST_VIEW')}
            >
              <ListIcon style={{ fontSize: 24, color: '#000' }} />
            </IconButton>
          )}
        </ListItemIcon>
      </ListItem>
      <Divider />
    </>
  )
}

function ProductList({ position }) {
  const [state, setState] = useReducer(reducer, { mode: '' })

  const history = useHistory()
  const classes = useStyles()
  const dispatch = useDispatch()
  const {
    loading,
    products: originalProducts,
    searchProductResults,
    fromSearch,
    config
  } = useSelector(selectProducts, shallowEqual)
  const { getLiteral } = useLiterals()

  const products = searchProductResults ? searchProductResults : originalProducts

  const handleSelectProduct = useCallback(
    (product) => {
      if (!Boolean(position.user?.lat) && !Boolean(position.user?.lng)) {
        position.handlerShowPositionDialog(true)
        return
      }

      if (config.multiProduct) dispatch(actions.selectProduct(product))

      const select = () => {
        history.push(`/produto/${getSlug(product.name)}/${product.gtin}`, {
          from: `${history.location.pathname}${history.location.search}`
        })
        track('selecionarProduto', { category: 'menu', name: product.name })
      }

      select()
    },
    [config, position]
  )

  const handleSelectAll = useCallback(() => {
    const searchAll = () => {
      track('buscarQualquerProduto', { name: 'todos', category: 'produto' })
      history.push('/produto/todos')
    }

    if (position.user?.lat && position.user?.lng) {
      searchAll()
    } else {
      position.handlerShowPositionDialog(true)
    }
  }, [position])

  const handleChangeView = (mode) => {
    ls.setItem('GOFIND:MODE_VIEW', mode)
    setState({ mode })
  }

  useEffect(() => {
    const localModeView = ls.getItem('GOFIND:MODE_VIEW')

    if (localModeView) return setState({ mode: localModeView })

    ls.setItem('GOFIND:MODE_VIEW', 'LIST_VIEW')
    setState({ mode: 'LIST_VIEW' })
  }, [])

  return (
    <Grid className={classes.wrapper}>
      {loading && <Placeholder />}

      {!loading && products && !products.length && fromSearch && (
        <Grid style={{ padding: 16 }}>{getLiteral('1884')}</Grid>
      )}

      {!loading && products && (
        <>
          {config.anyProducts === 'header' && (
            <AnyProduct
              state={state}
              classes={classes}
              onClick={handleSelectAll}
              onChange={handleChangeView}
              getLiteral={getLiteral}
            />
          )}

          <List
            id="product-list"
            classes={{ root: classes.root }}
            className={(state.mode === 'GRID_VIEW' && classes.grid) || ''}
          >
            {products
              .filter((product) => config?.featured?.some((feat) => feat.gtin === product.gtin))
              .map((product) => (
                <ProductItem
                  key={product.gtin}
                  product={product}
                  onSelectProduct={handleSelectProduct}
                  position={position}
                  mode={state.mode === 'GRID_VIEW'}
                />
              ))}

            {products
              .filter((product) => !config?.featured?.some((feat) => product.gtin === feat.gtin))
              .map((product) => (
                <ProductItem
                  key={product.gtin}
                  product={product}
                  onSelectProduct={handleSelectProduct}
                  position={position}
                  mode={state.mode === 'GRID_VIEW'}
                />
              ))}
          </List>

          {(!config.anyProducts || config.anyProducts === 'footer') && (
            <AnyProduct
              state={state}
              classes={classes}
              onClick={handleSelectAll}
              onChange={handleChangeView}
              getLiteral={getLiteral}
            />
          )}
        </>
      )}
    </Grid>
  )
}

export default ProductList
